import { useStore } from 'effector-react'
import { CarData, CarViewSettings } from 'features/car/types'
import React, { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Card } from 'ui/card/components'
import { Cell, CellSize, Row } from 'ui/grid'

import { $dealersShowMoreCars } from '../../features/filter/model'
import { useListSmartContractAPI } from '../../features/smart-contract/list/useListSmartContractAPI'
import { getProjectName } from '../../lib/get-project-name'
import { useAVNConfig } from '../../stages/AVN'

export type Props = {
  showSkeletons?: boolean
  skeletons?: React.ReactElement[]
  cars: CarData[] | null
  emptyText?: string
  cardSize: CellSize
  useSmartContract: boolean
  carViewSettings: CarViewSettings
  openCardsLinksInNewTab?: boolean
  showCreditPrice?: boolean
  showVideoIcon?: boolean
  showCarsNextDealer?: boolean
}

export const CarCardsList: FC<Props> = ({
  showSkeletons = false,
  skeletons,
  showCarsNextDealer,
  cars,
  emptyText,
  cardSize,
  carViewSettings,
  useSmartContract = false,
  openCardsLinksInNewTab = false,
  showCreditPrice = false,
  showVideoIcon
}) => {
  const { state } = useListSmartContractAPI()
  const { listBackground } = useAVNConfig()

  const [favoriteCars, setFavoriteCars] = useState([])
  const [isInitLK, setIsInitLK] = useState(false)

  useEffect(() => {
    const requestCarsEvent = new CustomEvent('getFavoriteCars')
    document.dispatchEvent(requestCarsEvent)
  }, [])

  const setCarsInLocalStorage = () => {
    const carsFav = localStorage.getItem('cars')
    // @ts-ignore
    const a = JSON.parse(carsFav)
    setFavoriteCars(a)
  }

  useEffect(() => {
    document.addEventListener('carsLoaded', setCarsInLocalStorage)
    return () => {
      document.removeEventListener('carsLoaded', setCarsInLocalStorage)
    }
  }, [])

  const addCars = (e: any) => {
    // @ts-ignore
    setFavoriteCars((prevState) => {
      return [...prevState, e.detail.car_id]
    })
  }

  useEffect(() => {
    document.addEventListener('newFavoriteCars', addCars)
    return () => {
      document.removeEventListener('newFavoriteCars', addCars)
    }
  }, [favoriteCars])

  const dealersShowMoreCars = useStore($dealersShowMoreCars)

  const setTextForNewCars = (index: string) => {
    const foundDealer = dealersShowMoreCars.dealers.find((dealer) => dealer[index])

    return foundDealer ? <NextDealerShowCarsText>{foundDealer[index]}</NextDealerShowCarsText> : null
  }

  const deleteCars = (e: any) => {
    setFavoriteCars((prevState) => {
      return prevState.filter((carId) => carId !== e.detail.car_id)
    })
  }

  useEffect(() => {
    document.addEventListener('deleteFavoriteCar', deleteCars)
    return () => {
      document.removeEventListener('deleteFavoriteCar', deleteCars)
    }
  }, [favoriteCars])

  useEffect(() => {
    document.addEventListener('successInit', (e) => {
      e && setIsInitLK(true)
    })
  })

  const prices = useMemo(() => {
    if (state && useSmartContract) {
      return state.cars.reduce((cars, car) => {
        return {
          ...cars,
          [car.carData.cardId]: {
            price: car.carData.price && car.carData.price > 0 ? car.carData.price : null,
            finalPrice: car.finalPrice && car.finalPrice > 0 ? car.finalPrice : null
          }
        }
      }, {} as Record<string, { price: number | null; finalPrice: number | null }>)
    }
    return {}
  }, [state])
  return (
    <Row>
      {showSkeletons && skeletons}
      {cars &&
        cars.map((car, i, array) => (
          <>
            {showCarsNextDealer && setTextForNewCars(i.toString())}
            <Cell {...cardSize} key={car.id} gutters={true}>
              <Card
                linkTarget={openCardsLinksInNewTab ? '_blank' : '_self'}
                smartContractEnabled={useSmartContract}
                carData={{
                  ...car,
                  isFavorite: favoriteCars.find((favoriteCarId: string) => favoriteCarId === car.id),
                  price: useSmartContract ? prices[car.id]?.price : car.price,
                  priceWithDiscounts:
                    useSmartContract && state?.tradeIn.behaviour !== 'enabled_without_calculations'
                      ? prices[car.id]?.finalPrice
                      : car.priceWithDiscounts
                }}
                carViewSettings={carViewSettings}
                showCreditPrice={showCreditPrice}
                showVideoIcon={car.has_video && showVideoIcon}
                onClick={(evt) => {
                  const node = evt.target.closest('[data-tip]')
                  if (!node) return
                  evt.preventDefault()
                }}
                isInitLK={isInitLK}
                listBackground={getProjectName() === 'tank' ? listBackground : null}
              />
            </Cell>
          </>
        ))}

      {emptyText && cars && !cars.length && (
        <Cell size={12}>
          <EmptyText marginBottom={20}>{emptyText}</EmptyText>
        </Cell>
      )}
    </Row>
  )
}

interface EmptyTextProps {
  marginBottom?: number
}

const NextDealerShowCarsText = styled.div`
  width: 100%;
  margin: 50px auto;
`

const EmptyText = styled.div<EmptyTextProps>`
  margin-top: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') {
      return theme.palette.white
    } else if (getProjectName() === 'generic_v2') {
      return '#D9D9D9'
    } else {
      return theme.palette.grey[900]
    }
  }};
  ${({ theme }) => theme.textStyles.p1};
  margin-bottom: ${({ marginBottom }) => marginBottom}px;
`
